import {useEffect,useMemo,useState} from 'react';
import {localBookingTimeToUTC} from './SchedulingWorkspace';
import {RoomFlexibleAvailabilityWeek} from './RoomFlexibleAvailabilityWeek';
import {RoomOperationsWorkspace} from './RoomOperationsWorkspace';
import {RoomFormattedText} from './RoomFormattedText';

type Location={code:string;name:string;timezone:string};
type Resource={resourceId:string;profileNumber:number;code:string;name:string;
  publicDescription:string|null;roomNotice:string|null;active:boolean;
  shownInOnlineBooking:boolean;scheduleColor:string|null;hasPhoto:boolean};
type RoomAppointment={
  appointmentId:string;customerName:string;status:string;serviceName:string;
  resourceId:string;startsAt:string;occupancyEndsAt:string;
};
type RoomBlock={
  blockId:string;resourceId:string;category:string;reason:string;
  startsAt:string;endsAt:string;
};
type AccessState='loading'|'ready'|'signin'|'denied'|'unavailable';
type RoomView='Schedule'|'Measurements'|'Availability'|'Availability Overrides'|'Logbook'|'Tasks';
type WeeklyWindow={weekday:number;opensMinute:number;closesMinute:number;
  flexibleBookingMinutes:number|null;incrementMinutes:number|null};
type Availability={resourceId:string;resourceName:string;timezone:string;
  weekly:WeeklyWindow[];notAvailableDates:string[];
  configurationSource:string;};
type WeekData={appointments:RoomAppointment[];blocks:RoomBlock[];blockStatus:AccessState};

function getLocalDay(instant:string,zone:string){
  const p=new Intl.DateTimeFormat('en-US',{timeZone:zone,year:'numeric',
    month:'2-digit',day:'2-digit'}).formatToParts(new Date(instant));
  const v=(part:string)=>p.find(x=>x.type===part)?.value??'';
  return v('year')+'-'+v('month')+'-'+v('day');
}
function minutes(instant:string,zone:string){
  const p=new Intl.DateTimeFormat('en-US',{timeZone:zone,hour:'2-digit',
    minute:'2-digit',hourCycle:'h23'}).formatToParts(new Date(instant));
  return Number(p.find(x=>x.type==='hour')?.value??0)*60+
    Number(p.find(x=>x.type==='minute')?.value??0);
}
function dayShift(iso:string,days:number){
  return new Date(Date.parse(iso+'T12:00:00Z')+days*86400000)
    .toISOString().slice(0,10);
}
function monday(iso:string){
  const day=new Date(iso+'T12:00:00Z').getUTCDay();
  return dayShift(iso,-((day+6)%7));
}
function monthFirst(iso:string){return iso.slice(0,7)+'-01';}
function monthLast(iso:string){
  const start=new Date(monthFirst(iso)+'T12:00:00Z');
  return new Date(Date.UTC(start.getUTCFullYear(),start.getUTCMonth()+1,0))
    .toISOString().slice(0,10);
}
function monthShift(iso:string,change:number){
  const first=new Date(monthFirst(iso)+'T12:00:00Z');
  return new Date(Date.UTC(first.getUTCFullYear(),first.getUTCMonth()+change,1))
    .toISOString().slice(0,10);
}
function dayTitle(iso:string){
  return new Intl.DateTimeFormat('en-US',{timeZone:'UTC',weekday:'short',
    month:'numeric',day:'numeric'}).format(new Date(iso+'T12:00:00Z'));
}
function clockInput(value:number){
  return String(Math.floor(value/60)).padStart(2,'0')+':'+
    String(value%60).padStart(2,'0');
}
function clockInputMinutes(value:string){
  const match=/^(\d{2}):(\d{2})$/.exec(value);
  return match?Number(match[1])*60+Number(match[2]):0;
}
function hourText(h:number){
  if(h===12)return '12pm';
  if(h<12)return h+'am';
  return (h-12)+'pm';
}
async function fetchAuthorized<T>(path:string,signal:AbortSignal){
  const result=await fetch(path,{credentials:'same-origin',cache:'no-store',signal});
  if(result.status===401)throw new Error('signin');
  if(result.status===403)throw new Error('denied');
  if(!result.ok)throw new Error('unavailable');
  const body=await result.json() as {ok:boolean;data:T};
  if(!body.ok)throw new Error('unavailable');
  return body.data;
}
function failureType(error:unknown):AccessState{
  if(error instanceof Error&&(error.message==='signin'||error.message==='denied'))
    return error.message;
  return 'unavailable';
}

/** Resource-level weekly schedule and independently stored owner-verified
 * flexible booking templates. No guest appointments are inferred from them.
 */
export function RoomsWorkspace({location,onGoCalendar,initialRoomId=null,canReorder=false}:{
  location:Location;onGoCalendar:()=>void;initialRoomId?:string|null;canReorder?:boolean;
}) {
  const [resources,setResources]=useState<Resource[]>([]);
  const [resourceState,setResourceState]=useState<AccessState>('loading');
  const [selectedRoom,setSelectedRoom]=useState<string|null>(null);
  const [reordering,setReordering]=useState(false);
  const [draftOrder,setDraftOrder]=useState<Resource[]>([]);
  const [orderStatus,setOrderStatus]=useState('');
  const [orderSaving,setOrderSaving]=useState(false);
  const [week,setWeek]=useState(()=>monday(getLocalDay(new Date().toISOString(),location.timezone)));
  const [refresh,setRefresh]=useState(0);
  const [schedule,setSchedule]=useState<WeekData>({appointments:[],blocks:[],blockStatus:'loading'});
  const [scheduleState,setScheduleState]=useState<AccessState>('loading');
  const [selectedAppointment,setSelectedAppointment]=useState<RoomAppointment|null>(null);
  const [view,setView]=useState<RoomView>('Schedule');
  const [overrideMonth,setOverrideMonth]=useState(()=>monthFirst(getLocalDay(
    new Date().toISOString(),location.timezone)));
  const [availability,setAvailability]=useState<Availability|null>(null);
  const [availabilityState,setAvailabilityState]=useState<AccessState>('loading');
  const [weeklyEdit,setWeeklyEdit]=useState<(WeeklyWindow & {original?:{weekday:number;opensMinute:number;closesMinute:number}})|null>(null);
  const [weeklyEditBusy,setWeeklyEditBusy]=useState(false);
  const [weeklyEditStatus,setWeeklyEditStatus]=useState('');
  const [copyFromOpen,setCopyFromOpen]=useState(false);
  const [copyBusy,setCopyBusy]=useState(false);
  const [editingRoom,setEditingRoom]=useState(false);
  const [roomDraft,setRoomDraft]=useState<Resource|null>(null);
  const [roomSaveBusy,setRoomSaveBusy]=useState(false);
  const [roomSaveStatus,setRoomSaveStatus]=useState('');
  const [photoDragging,setPhotoDragging]=useState(false);
  const [photoVersion,setPhotoVersion]=useState(0);

  useEffect(()=>{
    const abort=new AbortController();
    setResources([]);
    setSelectedRoom(null);
    setReordering(false);setDraftOrder([]);setOrderStatus('');
    setView('Schedule');setWeeklyEdit(null);setCopyFromOpen(false);
    setEditingRoom(false);setRoomDraft(null);
    setOverrideMonth(monthFirst(getLocalDay(new Date().toISOString(),location.timezone)));
    setResourceState('loading');
    void fetchAuthorized<{rooms:Resource[]}>(
      '/api/v1/staff/rooms/directory?'+new URLSearchParams({location:location.code}),
      abort.signal,
    ).then(result=>{
      if(abort.signal.aborted)return;
      setResources(result.rooms);
      setResourceState('ready');
      const route=window.location.pathname.match(/^\/rooms\/(\d+)-[^/]+(?:\/(.*))?$/);
      const matched=route?result.rooms.find(room=>room.profileNumber===Number(route[1])):null;
      setSelectedRoom(initialRoomId&&result.rooms.some(room=>room.resourceId===initialRoomId)
        ?initialRoomId:matched?.resourceId??result.rooms.find(room=>room.active)?.resourceId??
          result.rooms[0]?.resourceId??null);
      if(matched){
        setView(route?.[2]==='availability'?'Availability':
          route?.[2]==='availability-overrides'?'Availability Overrides':
          route?.[2]==='measurements'?'Measurements':
          route?.[2]==='logbook'?'Logbook':
          route?.[2]==='tasks'?'Tasks':'Schedule');
      }
    }).catch(error=>{if(!abort.signal.aborted)setResourceState(failureType(error));});
    return ()=>abort.abort();
  },[location.code,initialRoomId]);

  useEffect(()=>{
    const abort=new AbortController();
    setScheduleState('loading');
    setSchedule({appointments:[],blocks:[],blockStatus:'loading'});
    // Two contiguous subwindows handle either DST transition; the booking
    // calendar API will not accept a 7-day wall-clock interval spanning 169h.
    const windows=[[week,dayShift(week,4)],[dayShift(week,4),dayShift(week,7)]]
      .map(([from,to])=>new URLSearchParams({location:location.code,
        from:localBookingTimeToUTC(from+'T00:00',location.timezone),
        to:localBookingTimeToUTC(to+'T00:00',location.timezone)}).toString());
    void (async()=>{
      try{
        const appts=await Promise.all(windows.map(q=>fetchAuthorized<{
          appointments:RoomAppointment[];
        }>('/api/v1/staff/appointments?'+q,abort.signal)));
        if(abort.signal.aborted)return;
        const combined=[...new Map(appts.flatMap(result=>result.appointments)
          .map(appointment=>[appointment.appointmentId,appointment] as const))
          .values()];
        setScheduleState('ready');
        setSchedule({appointments:combined,blocks:[],blockStatus:'loading'});
        const blockResults=await Promise.allSettled(windows.map(q=>fetchAuthorized<{
          blocks:RoomBlock[];
        }>('/api/v1/staff/blocks?'+q,abort.signal)));
        if(abort.signal.aborted)return;
        if(blockResults.every(item=>item.status==='fulfilled')){
          setSchedule({appointments:combined,
            blocks:blockResults.flatMap(item=>item.status==='fulfilled'?item.value.blocks:[]),
            blockStatus:'ready'});
        }else{
          const rejected=blockResults.find(item=>item.status==='rejected');
          setSchedule({appointments:combined,blocks:[],
            blockStatus:rejected?.status==='rejected'
              ?failureType(rejected.reason):'unavailable'});
        }
      }catch(error){if(!abort.signal.aborted)setScheduleState(failureType(error));}
    })();
    return ()=>abort.abort();
  },[location.code,location.timezone,week,refresh]);

  // The existing permissioned API reads the room's own weekly windows and
  // dated Not Available overrides. It is not an inferred staff free/busy list.
  useEffect(()=>{
    const abort=new AbortController();
    setAvailability(null);setAvailabilityState('loading');
    if(!selectedRoom)return ()=>abort.abort();
    const params=new URLSearchParams({location:location.code,
      from:monthFirst(overrideMonth),to:monthLast(overrideMonth)});
    void fetchAuthorized<{room:Availability}>(
      '/api/v1/staff/rooms/'+encodeURIComponent(selectedRoom)+'/availability?'+params,
      abort.signal,
    ).then(result=>{
      if(abort.signal.aborted)return;
      if(result.room.resourceId!==selectedRoom)throw Error('unavailable');
      setAvailability(result.room);setAvailabilityState('ready');
    }).catch(error=>{if(!abort.signal.aborted)
      setAvailabilityState(failureType(error));});
    return ()=>abort.abort();
  },[location.code,selectedRoom,overrideMonth,refresh]);

  useEffect(()=>{
    const navigateBack=()=>{
      const route=window.location.pathname.match(/^\/rooms\/(\d+)-[^/]+(?:\/(.*))?$/);
      if(!route){
        setView('Schedule');return;
      }
      const room=resources.find(item=>item.profileNumber===Number(route[1]));
      if(!room)return;
      setSelectedRoom(room.resourceId);
      setView(route[2]==='measurements'?'Measurements':
        route[2]==='availability'?'Availability':
        route[2]==='availability-overrides'?'Availability Overrides':
        route[2]==='logbook'?'Logbook':
        route[2]==='tasks'?'Tasks':'Schedule');
    };
    window.addEventListener('popstate',navigateBack);
    return ()=>window.removeEventListener('popstate',navigateBack);
  },[resources]);
  const selected=resources.find(item=>item.resourceId===selectedRoom)??null;
  const dates=useMemo(()=>Array.from({length:7},(_,i)=>dayShift(week,i)),[week]);
  const rowHours=Array.from({length:14},(_,i)=>i+9);
  const height=13*58;
  const pos=(value:string)=>(minutes(value,location.timezone)-9*60)/60*58;
  const drawHeight=(from:string,to:string)=>Math.max(16,
    (Date.parse(to)-Date.parse(from))/60000/60*58);
  const appointments=schedule.appointments.filter(a=>a.resourceId===selectedRoom);
  const blocks=schedule.blocks.filter(a=>a.resourceId===selectedRoom);
  const weekEnd=dayShift(week,6);
  const monthStart=monthFirst(overrideMonth),monthEnd=monthLast(overrideMonth);
  const firstDow=new Date(monthStart+'T12:00:00Z').getUTCDay();
  const totalDays=Number(monthEnd.slice(8));
  const overrideDays=Array.from({length:Math.ceil((firstDow+totalDays)/7)*7},
    (_,i)=>i-firstDow+1);
  const closedDays=new Set(availability?.notAvailableDates??[]);

  const moveRoom=(index:number,direction:number)=>{
    setDraftOrder(current=>{
      const next=[...current],other=index+direction;
      if(other<0||other>=next.length)return current;
      [next[index],next[other]]=[next[other],next[index]];
      return next;
    });
  };
  const saveOrder=async()=>{
    setOrderSaving(true);setOrderStatus('');
    try{
      const response=await fetch('/api/v1/staff/calendar-resources/order',{
        method:'PATCH',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,
          resourceIds:draftOrder.map(room=>room.resourceId)}),
      });
      if(!response.ok)throw Error('Could not save room order.');
      const refreshed=await fetchAuthorized<{rooms:Resource[]}>(
        '/api/v1/staff/rooms/directory?'+new URLSearchParams({location:location.code}),
        new AbortController().signal);
      setResources(refreshed.rooms);setReordering(false);setOrderStatus('Room order saved.');
    }catch(error){setOrderStatus(error instanceof Error?error.message:'Unable to save order.');}
    finally{setOrderSaving(false);}
  };
  const saveWeekly=async(remove=false)=>{
    if(!selectedRoom||!weeklyEdit||!canReorder||weeklyEditBusy)return;
    if(!remove){
      const length=weeklyEdit.closesMinute-weeklyEdit.opensMinute;
      if(length<5||weeklyEdit.opensMinute%5!==0||weeklyEdit.closesMinute%5!==0){
        setWeeklyEditStatus('Start and end must be in 5-minute increments.');return;
      }
      if(weeklyEdit.incrementMinutes!==null &&
        (weeklyEdit.incrementMinutes<5||weeklyEdit.incrementMinutes>150||
          weeklyEdit.incrementMinutes%5!==0)){
        setWeeklyEditStatus('Choose a 5-minute increment from 5 to 150.');return;
      }
    }
    setWeeklyEditBusy(true);setWeeklyEditStatus('');
    try{
      const response=await fetch('/api/v1/staff/rooms/'+selectedRoom+'/availability',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,
          weekday:weeklyEdit.weekday,opensMinute:weeklyEdit.opensMinute,
          closesMinute:weeklyEdit.closesMinute,
          incrementMinutes:weeklyEdit.incrementMinutes,
          ...(weeklyEdit.original?{original:weeklyEdit.original}:{}),remove}),
      });
      if(!response.ok){
        const body=await response.json().catch(()=>null) as
          {error?:{code?:string;message?:string}}|null;
        throw Error(response.status===409
          ?'This exact time window is already saved for this room.'
          :'Room availability could not be saved: '+
            (body?.error?.message??body?.error?.code??response.status));
      }
      setWeeklyEdit(null);setWeeklyEditStatus('Room availability saved.');
      setRefresh(v=>v+1);
    }catch(error){setWeeklyEditStatus(error instanceof Error?error.message:
      'Unable to save room availability.');}
    finally{setWeeklyEditBusy(false);}
  };
  const navigateRoom=(room:Resource,tab:RoomView='Schedule')=>{
    setSelectedRoom(room.resourceId);setView(tab);setSelectedAppointment(null);
    setEditingRoom(false);setRoomSaveStatus('');
    const slug=room.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')
      .replace(/^-|-$/g,'');
    const suffix=tab==='Schedule'?'':tab==='Availability'?'/availability':
      tab==='Availability Overrides'?'/availability-overrides':
      tab==='Measurements'?'/measurements':tab==='Logbook'?'/logbook':'/tasks';
    window.history.pushState({},'','/rooms/'+room.profileNumber+'-'+slug+suffix);
  };
  const saveRoom=async()=>{
    if(!selected||!roomDraft||roomSaveBusy)return;
    setRoomSaveBusy(true);setRoomSaveStatus('');
    try{
      const response=await fetch('/api/v1/staff/rooms/'+selected.resourceId+'/profile',{
        method:'PATCH',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,
          name:roomDraft.name,publicDescription:roomDraft.publicDescription,
          roomNotice:roomDraft.roomNotice,active:roomDraft.active,
          shownInOnlineBooking:roomDraft.shownInOnlineBooking,
          scheduleColor:roomDraft.scheduleColor}),
      });
      if(!response.ok)throw Error('Room details could not be saved.');
      const body=await response.json() as {ok:boolean;data?:Resource};
      if(!body.ok||!body.data)throw Error('Invalid room save response.');
      setResources(old=>old.map(room=>room.resourceId===selected.resourceId?
        body.data!:room));
      setEditingRoom(false);setRoomSaveStatus('Room details saved.');
      setRefresh(v=>v+1);
    }catch(error){setRoomSaveStatus(error instanceof Error?
      error.message:'Room details could not be saved.');}
    finally{setRoomSaveBusy(false);}
  };
  const uploadRoomPhoto=async(file:File)=>{
    if(!selected||!canReorder)return;
    if(file.size>524288){setRoomSaveStatus('Photo must be 512 KB or smaller.');return;}
    setRoomSaveStatus('Uploading photo…');
    try{
      const response=await fetch('/api/v1/staff/rooms/'+selected.resourceId+
        '/photo?'+new URLSearchParams({location:location.code}),{
          method:'PUT',credentials:'same-origin',
          headers:{'content-type':file.type},body:file,
        });
      if(!response.ok)throw Error('Photo could not be saved.');
      setRoomSaveStatus('Photo saved.');setPhotoVersion(x=>x+1);
      setResources(old=>old.map(room=>room.resourceId===selected.resourceId?
        {...room,hasPhoto:true}:room));
    }catch(error){setRoomSaveStatus(error instanceof Error?
      error.message:'Photo could not be saved.');}
  };
  const copyFrom=async(sourceResourceId:string)=>{
    if(!selectedRoom||copyBusy)return;
    setCopyBusy(true);setWeeklyEditStatus('');
    try{
      const response=await fetch('/api/v1/staff/rooms/'+selectedRoom+
        '/availability/copy',{
          method:'POST',credentials:'same-origin',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({location:location.code,sourceResourceId}),
        });
      const body=await response.json().catch(()=>null) as
        {ok?:boolean;data?:{copiedWindows:number};error?:{code:string}}|null;
      if(!response.ok||!body?.ok){
        const code=body?.error?.code;
        if(code==='SOURCE_ROOM_HAS_NO_AVAILABILITY')
          throw Error('The selected room has no recurring availability to copy.');
        if(code==='ROOM_COPY_SCOPE_MISMATCH')
          throw Error('These rooms must be active and belong to the same location.');
        if(code==='ROOM_COPY_WINDOWS_OVERLAP')
          throw Error('This source has overlapping time windows that cannot be copied safely to this room. Adjust the source availability first.');
        if(code==='ACCESS_DENIED')
          throw Error('You do not have permission to copy room availability.');
        throw Error('Room availability could not be copied. Please try again.');
      }
      const name=resources.find(r=>r.resourceId===sourceResourceId)?.name??'selected room';
      setWeeklyEditStatus('Availability copied from '+name+
        ' ('+body.data?.copiedWindows+' windows).');
      setCopyFromOpen(false);setRefresh(v=>v+1);
    }catch(error){setWeeklyEditStatus(error instanceof Error?
      error.message:'Unable to copy room availability.');}
    finally{setCopyBusy(false);}
  };
  return <section className="ecfs-rooms-workspace" aria-label="Rooms">
    <div className="ecfs-room-title">
      <h1>Rooms</h1>
      {resourceState==='ready'&&canReorder&&
        <button type="button" className="ecfs-room-reorder-link"
          onClick={()=>{setReordering(current=>!current);
            setDraftOrder([...resources]);setOrderStatus('');}}>
          {reordering?'Cancel reorder':'Reorder'}
        </button>}
      {resourceState==='ready'&&<nav aria-label="Room list">
        {resources.map(room=><button type="button" key={room.resourceId}
          aria-current={room.resourceId===selectedRoom?'page':undefined}
          onClick={()=>navigateRoom(room)}>
          {room.name}{!room.active?' (Disabled)':''}
        </button>)}
      </nav>}
    </div>
    {reordering&&<section className="ecfs-room-order-editor" aria-label="Reorder rooms">
      <h2>Reorder rooms</h2>
      {draftOrder.map((room,index)=><div key={room.resourceId}>
        <span>{room.name}</span>
        <button type="button" aria-label={'Move '+room.name+' up'}
          disabled={index===0||orderSaving} onClick={()=>moveRoom(index,-1)}>↑</button>
        <button type="button" aria-label={'Move '+room.name+' down'}
          disabled={index===draftOrder.length-1||orderSaving}
          onClick={()=>moveRoom(index,1)}>↓</button>
      </div>)}
      <button type="button" disabled={orderSaving}
        onClick={()=>void saveOrder()}>{orderSaving?'Saving…':'Save order'}</button>
    </section>}
    {orderStatus&&<p role="status">{orderStatus}</p>}
    {resourceState==='loading'&&<p role="status">Loading authorized rooms…</p>}
    {resourceState==='signin'&&<p role="status">Sign in to view location rooms.</p>}
    {resourceState==='denied'&&<p role="alert">Room details require scheduling access.</p>}
    {resourceState==='unavailable'&&<p role="alert">Room directory is unavailable.</p>}
    {resourceState==='ready'&&!selected&&
      <p role="status">No active room resources have been configured for this location.</p>}
    {selected&&<>
      <div className="ecfs-room-detail-title">
        <h2>{selected.name}{!selected.active?' (Disabled)':''}</h2>
        {canReorder&&<button type="button" onClick={()=>{
          setRoomDraft({...selected});setEditingRoom(true);setRoomSaveStatus('');
        }}>Edit</button>}
        <a href={'/api/v1/staff/rooms/'+selected.resourceId+'/ical?'+
          new URLSearchParams({location:location.code})} download>
          <span aria-hidden="true">▣</span> iCal
        </a>
        <button type="button" onClick={onGoCalendar}>All rooms calendar</button>
      </div>
      {canReorder&&<div className="ecfs-room-photo-upload"
        onDragOver={e=>{e.preventDefault();setPhotoDragging(true);}}
        onDragLeave={()=>setPhotoDragging(false)}
        onDrop={e=>{e.preventDefault();setPhotoDragging(false);
          const file=e.dataTransfer.files[0];
          if(file)void uploadRoomPhoto(file);}}>
        <label className={photoDragging?'is-dragging':''}>
          {selected.hasPhoto?
            <img alt={selected.name+' photo'} src={'/api/v1/staff/rooms/'+
              selected.resourceId+'/photo?'+new URLSearchParams({
                location:location.code,v:String(photoVersion)})}/>
            :<span>Add room photo</span>}
          <input type="file" accept="image/png,image/jpeg,image/webp"
            aria-label={'Upload photo for '+selected.name}
            onChange={e=>{const file=e.target.files?.[0];
              if(file)void uploadRoomPhoto(file);
              e.target.value='';}}/>
        </label>
      </div>}
      {roomSaveStatus&&<p role="status">{roomSaveStatus}</p>}
      {editingRoom&&roomDraft&&canReorder&&<form className="ecfs-room-profile-editor"
        aria-label={'Edit '+selected.name}
        onSubmit={e=>{e.preventDefault();void saveRoom();}}>
        <h3>Edit {selected.name}</h3>
        <label>Room name
          <input value={roomDraft.name} maxLength={100} required
            onChange={e=>setRoomDraft(v=>v?{...v,name:e.target.value}:v)}/>
        </label>
        <label className="ecfs-room-metric-color">Room Metrics Plot Color
          <input aria-label="Room Metrics Plot Color" type="color"
            value={roomDraft.scheduleColor??'#9c9c9c'}
            onChange={e=>setRoomDraft(v=>v?{...v,scheduleColor:e.target.value}:v)}/>
          <small>Used to identify this room in room measurement graphs.</small>
        </label>
        <label>Room description
          <textarea rows={4} maxLength={4000}
            value={roomDraft.publicDescription??''}
            onChange={e=>setRoomDraft(v=>v?{...v,
              publicDescription:e.target.value||null}:v)}/>
        </label>
        <label>
          <input type="checkbox" checked={roomDraft.shownInOnlineBooking}
            onChange={e=>setRoomDraft(v=>v?{...v,
              shownInOnlineBooking:e.target.checked}:v)}/>
          Shown in online booking
        </label>
        <label>
          <input type="checkbox" checked={!roomDraft.active}
            onChange={e=>setRoomDraft(v=>v?{...v,active:!e.target.checked}:v)}/>
          Disable room
        </label>
        <button type="submit" disabled={roomSaveBusy}>
          {roomSaveBusy?'Saving…':'Update Room'}</button>
        <button type="button" onClick={()=>setEditingRoom(false)}>Cancel</button>
      </form>}
      {selected.publicDescription&&<RoomFormattedText content={selected.publicDescription}/>}
      <nav className="ecfs-room-tabs" aria-label={'Views for '+selected.name}>
        {(['Schedule','Measurements','Availability','Availability Overrides','Logbook','Tasks'] as const)
          .map(name=><button type="button" key={name}
            aria-current={name===view?'page':undefined}
            onClick={()=>navigateRoom(selected,name)}>
            {name}
          </button>)}
      </nav>
      {(view==='Availability'||view==='Availability Overrides')&&<>
        {availabilityState==='loading'&&<p role="status">Loading room availability…</p>}
        {availabilityState==='signin'&&<p role="status">Sign in to view room availability.</p>}
        {availabilityState==='denied'&&<p role="alert">
          Room availability requires scheduling access.</p>}
        {availabilityState==='unavailable'&&<p role="alert">
          Room availability has not been loaded. No available times can be inferred.
        </p>}
        {availabilityState==='ready'&&availability&&view==='Availability'&&
          <section className="ecfs-room-availability" aria-label="Recurring room Availability">
            <div className="ecfs-room-week-controls">
              <h3>Availability</h3>
              {canReorder&&<button type="button" disabled={copyBusy} onClick={()=>{
                setCopyFromOpen(v=>!v);setWeeklyEdit(null);
              }}>{copyBusy?'Copying…':'Copy From'}</button>}
            </div>
            {weeklyEditStatus&&<p role="status" aria-live="polite">{weeklyEditStatus}</p>}
            {copyFromOpen&&<div className="ecfs-room-copy-from" role="group"
              aria-label="Copy availability from another room">
              <p>Copy another room’s weekly availability to {availability.resourceName}.</p>
              {resources.filter(room=>room.resourceId!==selectedRoom&&room.active)
                .map(room=><button type="button" key={room.resourceId}
                  disabled={copyBusy}
                  onClick={()=>void copyFrom(room.resourceId)}>
                  {'Copy From '+room.name}
                </button>)}
              <button type="button" onClick={()=>setCopyFromOpen(false)}>
                Cancel</button>
            </div>}
            <p>Repeating availability for {availability.resourceName}. Click a block to edit it or select a new time.</p>
            <RoomFlexibleAvailabilityWeek roomName={availability.resourceName}
              windows={availability.weekly}
              onSelect={canReorder?(window,original)=>{
                setWeeklyEdit({...window,...(original?{original:{
                  weekday:window.weekday,opensMinute:window.opensMinute,
                  closesMinute:window.closesMinute}}:{})});
                setWeeklyEditStatus('');
              }:undefined}
              onCreate={canReorder?(weekday,opensMinute,closesMinute)=>{
                const isFloat=selected?.code.startsWith('FLOAT_ROOM_')===true;
                const end=isFloat
                  ?Math.min(1440,Math.max(closesMinute,opensMinute+120))
                  :closesMinute;
                setWeeklyEdit({weekday,opensMinute,closesMinute:end,
                  flexibleBookingMinutes:isFloat?Math.min(120,end-opensMinute):null,
                  incrementMinutes:isFloat?120:null});
                setWeeklyEditStatus('');
              }:undefined}/>
            {weeklyEdit&&canReorder&&<form className="ecfs-room-window-editor"
              role="dialog" aria-label="Edit room availability" onSubmit={e=>{
                e.preventDefault();void saveWeekly();}}>
              <h3>{weeklyEdit.original?'Edit availability':'Add availability'}</h3>
              {weeklyEditStatus&&<p role="alert" aria-live="assertive">
                {weeklyEditStatus}</p>}
              <label>Day <select aria-label="Availability day" value={weeklyEdit.weekday}
                onChange={e=>setWeeklyEdit(v=>v?{...v,weekday:Number(e.target.value)}:v)}>
                {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
                  .map((name,i)=><option key={name} value={i}>{name}</option>)}
              </select></label>
              <label>Start
                <input aria-label="Availability start time" type="time" step="300"
                  value={clockInput(weeklyEdit.opensMinute)}
                  onChange={e=>setWeeklyEdit(v=>v?{...v,
                    opensMinute:clockInputMinutes(e.target.value)}:v)}/>
              </label>
              <label>End
                <input aria-label="Availability end time" type="time" step="300"
                  value={clockInput(weeklyEdit.closesMinute)}
                  onChange={e=>setWeeklyEdit(v=>v?{...v,
                    closesMinute:clockInputMinutes(e.target.value)}:v)}/>
              </label>
              <label>Flexible Booking increment
                <select aria-label="Availability booking increment"
                  value={weeklyEdit.incrementMinutes??''}
                  onChange={e=>setWeeklyEdit(v=>{
                    if(!v)return v;
                    const increment=e.target.value===''?null:Number(e.target.value);
                    return {...v,incrementMinutes:increment,
                      flexibleBookingMinutes:increment};
                  })}>
                  <option value="">No increment (not flexible)</option>
                  {Array.from({length:30},(_,i)=>(i+1)*5).map(n=>
                    <option key={n} value={n}>{n} Minute Increment</option>)}
                </select>
              </label>
              <p className="ecfs-room-editor-hint">Choose any increment from
                5 through 150 minutes, in 5-minute steps.</p>
              <button type="submit" disabled={weeklyEditBusy}>Save Availability</button>
              {weeklyEdit.original&&<button type="button" disabled={weeklyEditBusy}
                onClick={()=>void saveWeekly(true)}>Remove Window</button>}
              <button type="button" onClick={()=>setWeeklyEdit(null)}>Cancel</button>
            </form>}
            <p className="ecfs-room-availability-description">
              Room availability is separate from the location's hours,
              service start times, existing appointments and one-time room slots.
            </p>
          </section>}
        {availabilityState==='ready'&&availability&&view==='Availability Overrides'&&
          <section className="ecfs-room-overrides" aria-label="Room Availability Overrides">
            <div className="ecfs-room-week-controls">
              <strong>{new Intl.DateTimeFormat('en-US',{month:'long',year:'numeric',
                timeZone:'UTC'}).format(new Date(monthStart+'T12:00:00Z'))}</strong>
              <div>
                <button type="button" onClick={()=>setOverrideMonth(v=>monthShift(v,-1))}>
                  ← Previous</button>
                <button type="button" onClick={()=>setOverrideMonth(monthFirst(
                  getLocalDay(new Date().toISOString(),location.timezone)))}>
                  Today</button>
                <button type="button" onClick={()=>setOverrideMonth(v=>monthShift(v,1))}>
                  Next →</button>
              </div>
            </div>
            <div className="ecfs-room-override-grid">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>
                <strong key={d}>{d}</strong>)}
              {overrideDays.map((d,i)=>{
                const date=d>0&&d<=totalDays?monthStart.slice(0,8)+
                  String(d).padStart(2,'0'):null;
                return <div key={i} className={date&&closedDays.has(date)?
                  'ecfs-room-override-closed':''}>
                  {date&&<><span>{d}</span>
                    {closedDays.has(date)&&<strong>Not Available</strong>}</>}
                </div>;
              })}
            </div>
            {!availability.notAvailableDates.length&&<p role="status">
              No Not Available dates are configured for this month.</p>}
          </section>}
      </>}
      {(view==='Measurements'||view==='Logbook'||view==='Tasks')&&
        <RoomOperationsWorkspace key={selected.resourceId+'-'+view}
          mode={view} roomId={selected.resourceId} roomName={selected.name}
          locationCode={location.code}/>}
      {view==='Schedule'&&<>
      <p className="ecfs-room-schedule-note">
        Appointments and existing room blocks. Recurring availability, flexible booking
        and temporary schedule overrides are separate operations and are not editable here yet.
      </p>
      <div className="ecfs-room-week-controls">
        <strong>{dayTitle(week)} – {dayTitle(weekEnd)}</strong>
        <div>
          <button type="button" onClick={()=>setWeek(v=>dayShift(v,-7))}>← Previous</button>
          <button type="button" onClick={()=>setWeek(monday(getLocalDay(
            new Date().toISOString(),location.timezone)))}>Today</button>
          <button type="button" onClick={()=>setWeek(v=>dayShift(v,7))}>Next →</button>
          <button type="button" onClick={()=>setRefresh(x=>x+1)}>Refresh</button>
        </div>
      </div>
      {scheduleState==='loading'&&<p role="status">Loading room appointments…</p>}
      {scheduleState==='signin'&&<p role="status">Sign in to view room appointments.</p>}
      {scheduleState==='denied'&&<p role="alert">You do not have appointment-read access.</p>}
      {scheduleState==='unavailable'&&<p role="alert">Weekly room schedule is unavailable.</p>}
      {scheduleState==='ready'&&<>
        {schedule.blockStatus!=='ready'&&<p role="status">
          Maintenance blocks could not be verified with your current permissions.
        </p>}
        <div className="ecfs-room-week-scroll">
          <div className="ecfs-room-week-grid" style={{
            gridTemplateColumns:'56px repeat(7,minmax(135px,1fr))'}}>
            <div/>
            {dates.map(date=><div key={date} className="ecfs-room-week-heading">
              {dayTitle(date)}
            </div>)}
            <div className="ecfs-room-week-hour-axis" style={{height}}>
              {rowHours.map(hour=><span key={hour}
                style={{top:(hour-9)*58}}>{hourText(hour)}</span>)}
            </div>
            {dates.map(date=><div key={date} className="ecfs-room-week-lane"
              style={{height,backgroundSize:'100% 58px'}}>
              {blocks.filter(b=>getLocalDay(b.startsAt,location.timezone)===date)
                .map(block=><div key={block.blockId} className="ecfs-room-week-block"
                  title={block.category+': '+block.reason}
                  style={{top:pos(block.startsAt),
                    height:drawHeight(block.startsAt,block.endsAt)}}>
                  {block.category==='UNAVAILABLE'?'Blocked Off':block.category}
                </div>)}
              {appointments.filter(a=>getLocalDay(a.startsAt,location.timezone)===date)
                .map(appt=><button type="button" key={appt.appointmentId}
                  className={'ecfs-room-week-appointment'+
                    (appt.status==='NO_SHOW'?' no-show':
                      appt.status==='COMPLETED'?' attendance-completed':'')}
                  style={{top:pos(appt.startsAt),
                    height:drawHeight(appt.startsAt,appt.occupancyEndsAt)}}
                  onClick={()=>setSelectedAppointment(appt)}>
                  <strong>{new Intl.DateTimeFormat('en-US',{
                    timeZone:location.timezone,hour:'numeric',minute:'2-digit',
                  }).format(new Date(appt.startsAt))}</strong>
                  <span>{appt.customerName}</span><small>{appt.serviceName}</small>
                </button>)}
            </div>)}
          </div>
        </div>
      </>}
      </>}
      {view==='Schedule'&&selectedAppointment&&<aside className="ecfs-room-week-preview" role="dialog"
        aria-label="Room appointment preview">
        <button type="button" aria-label="Close room appointment" onClick={()=>setSelectedAppointment(null)}>×</button>
        <h3>{selectedAppointment.customerName}</h3>
        <p>{selectedAppointment.serviceName} · {selectedAppointment.status}</p>
        <button type="button" onClick={onGoCalendar}>Open staff calendar</button>
      </aside>}
    </>}
  </section>;
}
